/** C1 — "Record hearing date" (gate G8 with the proposed date): date, time, court, hearing type, judge → Court Hearing on the diary
 *  (Emma's hearing-prep rows follow) and the route moves to "Awaiting court hearing". A 409 opens the G8 checklist. */
import { useState } from "react";
import { Gavel, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { recordPetitionHearing } from "@/lib/api";
import { CourtField, Field, errMsg, fmtDate, today } from "./petitionShared";

export default function HearingDateDialog({ caseRow, state, onClose, onSaved, onGate }) {
  const svc = state?.service;
  const relist = state?.route_status === "awaiting_hearing";   // C2: re-listed date after an adjournment "to a date to be fixed"
  const minDays = Number(state?.thresholds?.min_service_days_before_hearing || 14);
  const earliest = svc?.served_at ? new Date(new Date(svc.served_at).getTime() + minDays * 86400000).toISOString().slice(0, 10) : today();
  const [form, setForm] = useState({ date: "", time: "", court: state?.petition?.prepared?.court || state?.default_court || null, hearing_type: "first", judge: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const valid = !!form.date && !!form.court;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await recordPetitionHearing(caseRow.case_id, form);
      const n = (r.created_events || []).length;
      toast.success(`Hearing ${fmtDate(form.date)} ${relist ? "re-listed" : "diarised"}${n > 1 ? ` · ${n - 1} action${n - 1 === 1 ? "" : "s"}` : ""}${r.route ? ` · route → ${r.route.label || "Awaiting court hearing"}` : ""}`);
      onSaved?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) { toast.error(d.message); onGate?.(d.gate.gate, d.gate); }
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid="hearing-date-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><Gavel className="w-4 h-4 text-rose-700" />{relist ? "Record re-listed hearing date" : "Record hearing date"} — {caseRow.customer_name}</DialogTitle>
        </DialogHeader>
        {relist && <p className="text-[11px] text-amber-800 -mt-1" data-testid="hearing-relist-note">The last hearing was adjourned to a date to be fixed — this enters the court's new listing and closes the re-list chase.</p>}
        {svc?.served_at && <p className="text-[11px] text-muted-foreground -mt-1" data-testid="hearing-earliest">Petition served {fmtDate(svc.served_at)} — the hearing must be on or after <strong>{fmtDate(earliest)}</strong> ({minDays} days).</p>}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Hearing date"><Input type="date" value={form.date} min={earliest > today() ? earliest : today()} onChange={set("date")} className="h-8 text-xs" data-testid="hearing-date" /></Field>
          <Field label="Time (optional)"><Input type="time" value={form.time} onChange={set("time")} className="h-8 text-xs" data-testid="hearing-time" /></Field>
          <Field label="Hearing type">
            <Select value={form.hearing_type} onValueChange={set("hearing_type")}>
              <SelectTrigger className="h-8 text-xs" data-testid="hearing-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(state?.hearing_types || { first: "First hearing", adjourned: "Adjourned hearing" }).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Judge (if known)"><Input value={form.judge} onChange={set("judge")} className="h-8 text-xs" placeholder="e.g. ICC Judge Smith" data-testid="hearing-judge" /></Field>
          <Field label="Court" className="col-span-2" hint="Defaults to the petition court"><CourtField court={form.court} onChange={set("court")} testId="hearing-court" /></Field>
          <Field label="Notes" className="col-span-2"><Textarea rows={2} value={form.notes} onChange={set("notes")} className="text-xs" placeholder="Listing letter received…" data-testid="hearing-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="hearing-cancel">Cancel</Button>
          <Button size="sm" className="bg-rose-700 hover:bg-rose-800" onClick={submit} disabled={busy || !valid} data-testid="hearing-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Gavel className="w-3.5 h-3.5 mr-1" />}{relist ? "Record re-listed hearing" : "Record hearing · route → Awaiting court hearing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
